import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BankingPaymentRail,
  ConvenienceFeesResponse,
  ConvenienceFeeSetting,
  ELinxConnectionMethod,
  formattedCurrency,
} from 'vopay';
import vopayApi from '@/services/api.config';
import { usePaylink } from '@/providers/Paylink.provider';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';
import { useToken } from '@/providers/Token.provider';

const { api } = vopayApi;

export type FeeResult = {
  processingFee: string;
  transactionFee: string;
  processingFeeNumeric: number;
  transactionFeeNumeric: number;
  totalAmountNumeric: number;
  hasFees: boolean;
  transactionTypeName?: string;
};

export type FeeRange = {
  lowest: Omit<FeeResult, 'transactionTypeName' | 'totalAmountNumeric'> | null;
  highest: Omit<FeeResult, 'transactionTypeName' | 'totalAmountNumeric'> | null;
};

export default function useConvenienceFees() {
  const source = axios.CancelToken.source();
  const { flow, Token, paylink } = usePaylink();
  const [loading, setLoading] = useState(false);
  const [convenienceFees, setConvenienceFees] = useState<ConvenienceFeesResponse | null>(null);
  const { isFlexiblePayment } = useFlexiblePayment();
  const { token } = useToken();

  const showFees = useMemo(() => {
    if (!convenienceFees) return false;
    if (!convenienceFees.FeeSettings) return false;
    if (convenienceFees.FeeSettings.length === 0) return false;
    if (isFlexiblePayment) return false;
    return true;
  }, [convenienceFees, Token, flow]);
  useEffect(() => {
    return () => source.cancel();
  }, []);

  const fetchConvenienceFees = async (): Promise<ConvenienceFeesResponse> => {
    setLoading(true);
    try {
      const response = await api.fetchConvenienceFees(
        {
          Token,
        },
        source,
      );
      setConvenienceFees(response);
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getProcessingFee = (fee: {
    paymentMethod: ELinxConnectionMethod;
    province?: string;
    bankingPaymentRail?: BankingPaymentRail;
  }): FeeResult | null => {
    if (!convenienceFees) return null;

    if (!convenienceFees?.FeeSettings) return null;

    const { paymentMethod, province, bankingPaymentRail } = fee;

    // Get transaction type from paylink data
    const transactionType = paylink.PaymentType === 'fund' ? 'funding' : 'withdrawal';

    // Map payment methods to transaction types based on banking payment rail
    // NOTE: these values come from the b/e and are used as keys. DO NOT TRANSLATE
    const transactionTypeMap: Record<ELinxConnectionMethod, { funding: string; withdrawal: string }> = {
      bank: {
        funding:
          bankingPaymentRail === 'ach'
            ? 'ACH Funding'
            : bankingPaymentRail === 'fedwire'
              ? 'Fedwire Funding'
              : bankingPaymentRail === 'rtp'
                ? 'RTP Funding'
                : bankingPaymentRail === 'vopayinstant'
                  ? 'VoPay Instant Funding'
                  : 'EFT Funding',
        withdrawal:
          bankingPaymentRail === 'ach'
            ? 'ACH Withdrawal'
            : bankingPaymentRail === 'fedwire'
              ? 'Fedwire Withdrawal'
              : bankingPaymentRail === 'rtp'
                ? 'RTP Withdrawal'
                : bankingPaymentRail === 'vopayinstant'
                  ? 'VoPay Instant Withdrawal'
                  : 'EFT Withdrawal',
      },
      credit: {
        funding: 'Credit Card',
        withdrawal: 'Credit Card',
      },
      debitcard: {
        funding: 'AFT',
        withdrawal: 'OCT',
      },
      email: {
        funding: 'Interac Money Request',
        withdrawal: 'Interac Bulk Payout',
      },
      googlepay: {
        funding: 'Google Pay',
        withdrawal: 'Google Pay',
      },
      applepay: {
        funding: 'Apple Pay',
        withdrawal: 'Apple Pay',
      },
      paypal: {
        funding: 'PayPal Funding',
        withdrawal: 'PayPal Withdrawal',
      },
      venmo: {
        funding: 'Venmo Funding',
        withdrawal: 'Venmo Withdrawal',
      },
    };

    const transactionTypeName = transactionTypeMap[paymentMethod]?.[transactionType];

    if (!transactionTypeName) {
      return {
        processingFee: '0',
        transactionFee: '0',
        processingFeeNumeric: 0,
        transactionFeeNumeric: 0,
        totalAmountNumeric: calculateTotalAmount(0, 0),
        hasFees: false,
      };
    }

    // Find the fee setting for this transaction type
    const feeSetting = Object.values(convenienceFees.FeeSettings).find(
      (setting) => setting.TransactionType === transactionTypeName,
    );

    if (!feeSetting) {
      return {
        processingFee: '0',
        transactionFee: '0',
        processingFeeNumeric: 0,
        transactionFeeNumeric: 0,
        totalAmountNumeric: calculateTotalAmount(0, 0),
        hasFees: false,
      };
    }

    if (province) {
      const hasProvinceRestriction = convenienceFees.Restrictions.some(
        (restriction) => restriction.Province === province || restriction.ProvinceAbbreviation === province,
      );

      if (hasProvinceRestriction) {
        return {
          processingFee: '0',
          transactionFee: '0',
          processingFeeNumeric: 0,
          transactionFeeNumeric: 0,
          totalAmountNumeric: calculateTotalAmount(0, 0),
          hasFees: false,
        };
      }
    }

    // Format processing fee (always percentage)
    const processingFeeNumeric = parseFloat(feeSetting.ProcessingFee);
    let processingFee = '';

    if (processingFeeNumeric === 0) {
      processingFee = '0';
    } else {
      // Processing fee is always a percentage
      processingFee = `${(processingFeeNumeric * 100).toFixed(2)}%`;
    }

    // Format transaction fee (always amount)
    const transactionFeeNumeric = parseFloat(feeSetting.TransactionFee);
    let transactionFee = '';

    if (transactionFeeNumeric === 0) {
      transactionFee = '0';
    } else {
      // Transaction fee is always an amount, use formattedCurrency with paylink currency
      const currency = paylink.Currency || 'USD';
      const formattedAmount = formattedCurrency(transactionFeeNumeric.toString());
      transactionFee = `${currency} ${formattedAmount}`;
    }

    return {
      processingFee,
      transactionFee,
      processingFeeNumeric,
      transactionFeeNumeric,
      transactionTypeName,
      totalAmountNumeric: calculateTotalAmount(processingFeeNumeric, transactionFeeNumeric),
      hasFees: processingFeeNumeric > 0 || transactionFeeNumeric > 0,
    };
  };

  function calculateTotalAmount(processingFee: number, transactionFee: number) {
    const baseAmount = parseFloat(paylink.Amount || '0');

    if (processingFee === 0 && transactionFee === 0) return baseAmount;

    if (paylink.PaymentType === 'withdraw') {
      // Receiving formula: subtract fees from base amount
      return baseAmount * (1 - processingFee) - transactionFee;
    }

    // Sending formula: add fees to base amount
    return baseAmount * (1 + processingFee) + transactionFee;
  }

  const getPaymentMethodTransactionTypes = (paymentMethod: ELinxConnectionMethod, country: string): string[] => {
    const paymentMethodMap: Record<ELinxConnectionMethod, string[]> = {
      bank: country === 'US' ? ['ACH Funding', 'ACH Withdrawal'] : ['EFT Funding', 'EFT Withdrawal'],
      credit: ['Credit Card'],
      debitcard: ['AFT', 'OCT'],
      email: ['Interac Money Request', 'Interac Bulk Payout'],
      googlepay: ['Google Pay'],
      applepay: ['Apple Pay'],
      paypal: ['PayPal Withdrawal'],
      venmo: ['Venmo Withdrawal'],
    };
    return paymentMethodMap[paymentMethod] || [];
  };

  const createZeroFeeSetting = (transactionType: string): ConvenienceFeeSetting => ({
    TransactionType: (transactionType || '') as ConvenienceFeeSetting['TransactionType'],
    ProcessingFee: '0',
    TransactionFee: '0',
  });

  const formatFeeResult = (processingFee: string, transactionFee: string) => {
    const processingFeeNumeric = parseFloat(processingFee);
    const transactionFeeNumeric = parseFloat(transactionFee);
    const currency = paylink.Currency || 'USD';

    return {
      processingFee: processingFeeNumeric === 0 ? '0' : `${(processingFeeNumeric * 100).toFixed(2)}%`,
      transactionFee: transactionFeeNumeric === 0 ? '0' : `${currency} ${formattedCurrency(transactionFee)}`,
      processingFeeNumeric,
      transactionFeeNumeric,
      hasFees: processingFeeNumeric > 0 || transactionFeeNumeric > 0,
    };
  };

  const getLowestAndHighestFees = (paymentMethod: ELinxConnectionMethod): FeeRange => {
    if (!convenienceFees?.FeeSettings) {
      return { lowest: null, highest: null };
    }

    const country = token?.Country || 'US';
    const methodTransactionTypes = getPaymentMethodTransactionTypes(paymentMethod, country);

    const methodFeeSettings = Object.values(convenienceFees.FeeSettings).filter((setting) =>
      methodTransactionTypes.includes(setting.TransactionType),
    );

    // Include zero fee if not all expected transaction types are present
    const allFeeSettings =
      methodFeeSettings.length < methodTransactionTypes.length
        ? [...methodFeeSettings, createZeroFeeSetting(methodTransactionTypes[0])]
        : methodFeeSettings;

    if (allFeeSettings.length === 0) {
      return { lowest: null, highest: null };
    }

    // Find lowest fees independently
    const lowestProcessingFee = allFeeSettings.reduce((min, curr) =>
      parseFloat(curr.ProcessingFee) < parseFloat(min.ProcessingFee) ? curr : min,
    ).ProcessingFee;

    const lowestTransactionFee = allFeeSettings.reduce((min, curr) =>
      parseFloat(curr.TransactionFee) < parseFloat(min.TransactionFee) ? curr : min,
    ).TransactionFee;

    // Find highest fees (for bank, exclude base ACH/EFT types)
    const getHighestFeeSettings = () => {
      if (paymentMethod === 'bank') {
        const allBankingTypes = [
          'EFT Funding',
          'EFT Withdrawal',
          'ACH Funding',
          'ACH Withdrawal',
          'Fedwire Funding',
          'Fedwire Withdrawal',
          'RTP Funding',
          'RTP Withdrawal',
          'VoPay Instant Funding',
          'VoPay Instant Withdrawal',
        ];
        const allBankingSettings = Object.values(convenienceFees.FeeSettings).filter((s) =>
          allBankingTypes.includes(s.TransactionType),
        );
        const baseTypes = country === 'US' ? ['ACH Funding', 'ACH Withdrawal'] : ['EFT Funding', 'EFT Withdrawal'];
        const nonBaseSettings = allBankingSettings.filter((s) => !baseTypes.includes(s.TransactionType));
        return nonBaseSettings.length > 0 ? nonBaseSettings : allFeeSettings;
      }
      return allFeeSettings;
    };

    const highestFeeSettings = getHighestFeeSettings();
    const highestProcessingFee = highestFeeSettings.reduce((max, curr) =>
      parseFloat(curr.ProcessingFee) > parseFloat(max.ProcessingFee) ? curr : max,
    ).ProcessingFee;

    const highestTransactionFee = highestFeeSettings.reduce((max, curr) =>
      parseFloat(curr.TransactionFee) > parseFloat(max.TransactionFee) ? curr : max,
    ).TransactionFee;

    return {
      lowest: formatFeeResult(lowestProcessingFee, lowestTransactionFee),
      highest: formatFeeResult(highestProcessingFee, highestTransactionFee),
    };
  };

  return {
    loading,
    convenienceFees,
    fetchConvenienceFees,
    getProcessingFee,
    getLowestAndHighestFees,
    showFees,
  };
}
